import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  differenceInDays,
  format,
  parseISO,
  startOfDay,
  subDays,
} from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import ActivityService, {
  ConsultantActivityRange,
} from "../../api/ActivityService";
import CircularLoader from "../../common/components/fundamentals/Loader/CircularLoader";
import { borderStyle, defaultTransition } from "../../styles/consts";
import ActivityHeatStrip, { HeatLegend } from "./ActivityHeatStrip";
import DateRangePopover from "./DateRangePopover";

const STORAGE_KEY = "activity-range";

interface StoredRange {
  start: string;
  end: string;
}

const today = () => startOfDay(new Date());

const getStoredRange = (): { start: Date; end: Date } => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: StoredRange = JSON.parse(raw);
      return { start: parseISO(parsed.start), end: parseISO(parsed.end) };
    }
  } catch {
  }
  localStorage.removeItem("activity-view-mode");
  return { start: subDays(today(), 7), end: today() };
};

const persistRange = (start: Date, end: Date) => {
  const stored: StoredRange = {
    start: format(start, "yyyy-MM-dd"),
    end: format(end, "yyyy-MM-dd"),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
};

const PRESETS: { label: string; days: number }[] = [
  { label: "1d", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

const presetMatches = (start: Date, end: Date, days: number): boolean => {
  const t = today();
  if (format(end, "yyyy-MM-dd") !== format(t, "yyyy-MM-dd")) return false;
  return differenceInDays(end, start) === days;
};

export default function ActivityTab() {
  const [{ start, end }, setRange] = useState(getStoredRange);
  const [selectedConsultantIds, setSelectedConsultantIds] = useState<string[]>([]);
  const [data, setData] = useState<ConsultantActivityRange[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [calculating, setCalculating] = useState<boolean>(false);
  const [showSupervisorDetails, setShowSupervisorDetails] = useState<boolean>(() => {
    return localStorage.getItem("activity-show-supervisor-details") === "true";
  });
  const [showInactive, setShowInactive] = useState<boolean>(() => {
    return localStorage.getItem("activity-show-inactive") === "true";
  });

  const abortRef = useRef<AbortController | null>(null);

  const consultantOptions = useMemo(
    () => data.map((d) => ({ id: d.consultant_id, name: d.consultant_name })),
    [data]
  );

  const visibleData = useMemo(() => {
    let rows = data;

    if (selectedConsultantIds.length > 0) {
      const wanted = new Set(selectedConsultantIds);
      rows = rows.filter((d) => wanted.has(d.consultant_id));
    } else if (!showInactive) {
      rows = rows.filter((d) => d.current_status === "active");
    }

    return [...rows].sort((a, b) => {
      if (b.matches_received !== a.matches_received) {
        return b.matches_received - a.matches_received;
      }
      return a.consultant_name.localeCompare(b.consultant_name);
    });
  }, [data, selectedConsultantIds, showInactive]);

  const toggleSupervisorDetails = (next: boolean) => {
    setShowSupervisorDetails(next);
    localStorage.setItem("activity-show-supervisor-details", String(next));
  };

  const toggleShowInactive = (next: boolean) => {
    setShowInactive(next);
    localStorage.setItem("activity-show-inactive", String(next));
  };

  const setRangeAndPersist = (newStart: Date, newEnd: Date) => {
    setRange({ start: newStart, end: newEnd });
    persistRange(newStart, newEnd);
  };

  const onPresetClick = (days: number) => {
    const newEnd = today();
    const newStart = subDays(newEnd, days);
    setRangeAndPersist(newStart, newEnd);
  };

  const fetchActivity = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const startStr = format(start, "yyyy-MM-dd");
      const endStr = format(end, "yyyy-MM-dd");
      const response = await ActivityService.getActivityRange(
        startStr,
        endStr,
        undefined,
        controller.signal,
      );

      if (controller.signal.aborted) return;
      if (response.success) {
        setData(response.data);
      } else {
        setError(response.message || "Failed to load activity data");
      }
    } catch (err: any) {
      if (controller.signal.aborted) return;
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      console.error("Error fetching activity:", err);
      setError(err.message || "Failed to load activity data");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setCalculating(true);
      setError(null);

      const response = await ActivityService.calculateActivity(1);
      if (response.success) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await fetchActivity();
      } else {
        setError("Failed to recalculate today's activity");
      }
    } catch (err: any) {
      console.error("Error calculating activity:", err);
      setError(err.message || "Failed to calculate activity");
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box
          sx={{
            marginBottom: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              Consultant Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {format(start, "dd.MM.yyyy")} — {format(end, "dd.MM.yyyy")}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={0.5}>
              {PRESETS.map((p) => (
                <Chip
                  key={p.label}
                  label={p.label}
                  size="small"
                  color={presetMatches(start, end, p.days) ? "primary" : "default"}
                  onClick={() => onPresetClick(p.days)}
                  sx={{
                    cursor: "crosshair",
                    transition: defaultTransition,
                    "&:hover": { color: "#ff5950" },
                  }}
                />
              ))}
            </Stack>

            <DateRangePopover
              start={start}
              end={end}
              onChange={(s, e) => setRangeAndPersist(s, e)}
            />

            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 240 }}
              options={consultantOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={consultantOptions.filter((o) =>
                selectedConsultantIds.includes(o.id)
              )}
              onChange={(_, value) =>
                setSelectedConsultantIds(value.map((v) => v.id))
              }
              renderInput={(params) => (
                <TextField {...params} label="Filter consultants" />
              )}
            />

            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={calculating || loading}
            >
              {calculating ? "Refreshing..." : "Refresh"}
            </Button>
          </Stack>
        </Box>

        {error && (
          <Box
            sx={{
              marginBottom: 2,
              padding: 2,
              backgroundColor: "#ffebee",
              borderRadius: 1,
            }}
          >
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {loading || calculating ? (
          <CircularLoader />
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showInactive}
                    onChange={(e) => toggleShowInactive(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Show inactive</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showSupervisorDetails}
                    onChange={(e) => toggleSupervisorDetails(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Supervisor details</Typography>}
              />
            </Box>
            {visibleData.length === 0 ? (
              <Box sx={{ padding: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No activity data for the selected range.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ border: borderStyle, boxShadow: "none" }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Consultant</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: 220 }}>
                        Activity in range
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Matches Received ↓
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Pending
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Confirmed
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Rejected
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Timed Out
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Avg Response Time (h:mm)
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Supervisor</TableCell>
                      {showSupervisorDetails && (
                        <>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Pending at Supervisor
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Supervisor Avg Response (h:mm)
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Sent Back
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Sent to Sales
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Sent Out
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleData.map((row) => {
                      const inactive = row.current_status !== "active";
                      return (
                        <TableRow
                          key={row.consultant_id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            ...(inactive ? { backgroundColor: "#fafafa", color: "#666" } : {}),
                          }}
                        >
                          <TableCell sx={{ color: inactive ? "#666" : undefined }}>
                            {row.consultant_name}{" "}
                            <Chip
                              size="small"
                              label={inactive ? "inactive" : "active"}
                              sx={{
                                ml: 0.5,
                                height: 18,
                                fontSize: 10,
                                backgroundColor: inactive ? "#9e9e9e" : "#2e7d32",
                                color: "#fff",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <ActivityHeatStrip
                              dailyMatches={row.daily_matches}
                              start={start}
                              end={end}
                            />
                          </TableCell>
                          <TableCell align="center">{row.matches_received}</TableCell>
                          <TableCell align="center">{row.pending_consultant}</TableCell>
                          <TableCell align="center">{row.confirmed}</TableCell>
                          <TableCell align="center">{row.rejected}</TableCell>
                          <TableCell align="center">{row.timed_out}</TableCell>
                          <TableCell align="center">{row.avg_response_time}</TableCell>
                          <TableCell>{row.supervisor_name}</TableCell>
                          {showSupervisorDetails && (
                            <>
                              <TableCell align="center">{row.pending_supervisor}</TableCell>
                              <TableCell align="center">
                                {row.supervisor_avg_response_time}
                              </TableCell>
                              <TableCell align="center">{row.sent_back}</TableCell>
                              <TableCell align="center">{row.sent_to_sales}</TableCell>
                              <TableCell align="center">{row.sent_out}</TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <Box
                  sx={{
                    p: 1,
                    backgroundColor: "#fafafa",
                    fontSize: 11,
                    color: "#666",
                  }}
                >
                  <HeatLegend />
                </Box>
              </TableContainer>
            )}
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
}
